import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { Mail, ArrowLeft, Send, CheckCircle, Loader2 } from "lucide-react"
import { getPasswordResetToken } from "../../services/operations/authAPI"

function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.auth)

  const handleOnSubmit = (e) => {
    e.preventDefault()
    dispatch(getPasswordResetToken(email, setEmailSent))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-md">
        {/* Modern Header */}
        <div className="text-center mb-4">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              {!emailSent ? (
                <Mail className="h-6 w-6 text-white" />
              ) : (
                <CheckCircle className="h-6 w-6 text-white" />
              )}
            </div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
            {!emailSent ? "Reset Your Password" : "Check Your Email"}
          </h1>
          <p className="text-slate-600 leading-relaxed">
            {!emailSent
              ? "No worries! We'll send you reset instructions to get you back into your account."
              : `We've sent password reset instructions to ${email}`}
          </p>
        </div>

        {/* Form Container */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/50 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
            <form onSubmit={handleOnSubmit} className="space-y-6">
              {!emailSent && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-slate-700 font-semibold">
                    <Mail className="h-4 w-4 text-blue-500" />
                    Email Address
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full bg-white/80 backdrop-blur-sm border border-white/30 rounded-xl p-4 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 shadow-lg transition-all duration-300"
                  />
                </div>
              )}

              <button 
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed font-semibold text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5" />
                    Sending...
                  </>
                ) : !emailSent ? (
                  <>
                    <Send className="h-5 w-5" />
                    Send Reset Instructions
                  </>
                ) : (
                  <>
                    <Mail className="h-5 w-5" />
                    Resend Email
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <Link to="/login">
                <button className="text-slate-600 hover:text-blue-600 transition-colors duration-200 flex items-center gap-2 mx-auto font-medium">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Login
                </button>
              </Link>
            </div>

            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Don't have an account?{" "}
                <Link to="/signup" className="text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200">
                  Sign up here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;